import React from 'react';
import { FiTarget, FiHeart, FiEye, FiInfo, FiAward, FiCheckCircle, FiBook, FiUsers, FiMonitor, FiCpu, FiTrendingUp, FiStar, FiZap, FiShield, FiGlobe, FiSmile, FiPhone } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';

const About = () => {
  const facilities = [
    {
      icon: FiCpu,
      title: "Robotics Lab",
      description: "State-of-the-art robotics equipment for hands-on STEM learning and innovation, fostering critical thinking and problem-solving skills in young learners.",
      color: "from-blue-400 to-indigo-500",
      image: "/img/why/robotics.jpeg"
    },
    {
      icon: FiBook,
      title: "English / Math Lab",
      description: "Specialized interactive labs for language development and mathematical concepts, using modern tools and engaging activities to build strong foundations.",
      color: "from-orange-400 to-red-500",
      image: "/img/why/english.jpeg"
    },
    {
      icon: FiBook,
      title: "Digital Library",
      description: "Well-stocked library with diverse collection of books, educational materials, and reading spaces that encourage literacy and a love for learning.",
      color: "from-purple-400 to-pink-500",
      image: "/img/why/library.jpeg"
    },
    {
      icon: FiMonitor,
      title: "Computer Lab",
      description: "Modern computer facilities equipped with age-appropriate software and hardware for digital literacy, coding basics, and technology integration.",
      color: "from-green-400 to-teal-500",
      image: "/img/why/computer.jpeg"
    }
  ];

  const learningApproach = [
    { title: 'Toys & Games', description: 'Fun tools to support interactive learning', icon: FiSmile, color: 'rose' },
    { title: 'Theory', description: 'Essential knowledge children need to know', icon: FiBook, color: 'amber' },
    { title: 'Timetable', description: 'Well-planned daily routine for consistency', icon: FiCheckCircle, color: 'blue' },
    { title: 'Training', description: 'Well-trained educators ensuring quality', icon: FiUsers, color: 'emerald' },
    { title: 'Technology', description: 'Apps and digital tools to enhance learning', icon: FiCpu, color: 'indigo' },
    { title: 'Teamwork', description: 'Collaborative learning and social skills', icon: FiHeart, color: 'yellow' }
  ];

  const whyChooseUs = [
    { icon: FiUsers, title: 'Expert Educators', description: 'Highly trained and passionate educators providing personalized attention to every child', gradient: 'from-purple-500 to-indigo-600' },
    { icon: FiBook, title: 'Holistic Curriculum', description: 'Balanced focus on intellectual, emotional, social, and physical development', gradient: 'from-blue-500 to-cyan-600' },
    { icon: FiShield, title: 'Safe Environment', description: 'Secure, welcoming space encouraging exploration and growth with 24/7 security', gradient: 'from-green-500 to-emerald-600' },
    { icon: FiHeart, title: 'Parent Partnership', description: 'Open communication and collaboration for holistic child development', gradient: 'from-pink-500 to-rose-600' },
    { icon: FiZap, title: 'Innovative Learning', description: 'Latest educational technologies for engaging and effective learning', gradient: 'from-orange-500 to-red-600' },
    { icon: FiGlobe, title: 'CBSE Excellence', description: 'Following recognized CBSE framework from Nursery to Grade 7', gradient: 'from-teal-500 to-cyan-600' }
  ];

  const awards = [
    {
      icon: FiAward,
      title: "ISO 9001:2015 Certified",
      description: "International ISO certification ensuring highest standards in education delivery",
      image: "/img/awards/1.jpg",
      gradient: "from-blue-600 to-indigo-600"
    },
{
  icon: FiStar,
  title: "Excellence in Education Award 2025",
  description: "Recognized for outstanding academic performance, innovative teaching methods, and commitment to holistic student development",
  image: "/img/awards/2.jpg",
  gradient: "from-yellow-600 to-orange-600"
},
    {
      icon: FiTrendingUp,
      title: "Excellence in STEM Education",
      description: "Outstanding contributions to STEM education and robotics programs",
      image: "/img/awards/3.jpg",
      gradient: "from-green-600 to-emerald-600"
    }
  ];

  const stats = [
    { number: '500+', label: 'Happy Students', icon: FiSmile, gradient: 'from-purple-500 to-indigo-600' },
    { number: '50+', label: 'Expert Educators', icon: FiUsers, gradient: 'from-blue-500 to-cyan-600' },
    { number: '3+', label: 'Major Awards', icon: FiAward, gradient: 'from-yellow-500 to-orange-600' },
    { number: '100%', label: 'Parent Satisfaction', icon: FiHeart, gradient: 'from-pink-500 to-rose-600' }
  ];

  return (
    <div className="animate-fadeIn bg-white">
      <SEO page="about" />
      
      {/* Simple Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 to-indigo-900 pt-32 pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Hero Content */}
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-lg px-6 py-3 rounded-full border border-white/20 mb-8" data-aos="fade-up">
              <SafeIcon icon={FiHeart} className="w-5 h-5 text-white" />
              <span className="text-white font-semibold text-sm">Premium Early Childhood Education</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight" data-aos="fade-up" data-aos-delay="100">
              Nurturing Young Minds With
              <span className="block text-white/90 mt-2">
                Care, Creativity & Curiosity
              </span>
            </h1>

            <p className="text-xl text-white/80 leading-relaxed mb-10 max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="200">
              Where every child's potential is discovered, nurtured, and celebrated through innovative education and compassionate care.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12" data-aos="fade-up" data-aos-delay="300">
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 px-6 py-3 rounded-2xl">
                <span className="text-white font-semibold">Nursery to Grade 7</span>
              </div>
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 px-6 py-3 rounded-2xl">
                <span className="text-white font-semibold">Academic Year 2026</span>
              </div>
              <div className="bg-emerald-500 px-6 py-3 rounded-2xl">
                <span className="text-white font-semibold">✓ Govt. Recognised</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-aos="fade-up" data-aos-delay="400">
              <a
                href="/contact"
                className="inline-flex items-center justify-center space-x-2 bg-white text-blue-900 px-8 py-4 rounded-2xl font-bold hover:bg-blue-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                <span>Schedule a Tour</span>
                <SafeIcon icon={FiCheckCircle} className="w-5 h-5" />
              </a>
              <a
                href="tel:+918106921133"
                className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-lg border-2 border-white text-white px-8 py-4 rounded-2xl font-bold hover:bg-white/20 transform hover:scale-105 transition-all duration-200"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>81069 21133</span>
              </a>
            </div>
          </div>
        </div>

        {/* Simple Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* Welcome Section - Redesigned */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-50 rounded-full filter blur-3xl opacity-50 -mr-48 -mt-48"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Side */}
            <div data-aos="fade-right" className="relative">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl opacity-20 blur-2xl"></div>
                <img 
                  src="/img/about/about.png" 
                  alt="Children Learning at THE ASIAN INTERNATIONAL SCHOOL" 
                  className="relative rounded-3xl shadow-2xl w-full h-[600px] object-cover border-8 border-white"
                />
                {/* Floating Stats Card */}
                <div className="absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-2xl p-6 border-4 border-purple-100" data-aos="fade-up" data-aos-delay="200">
                  <div className="text-5xl font-black text-purple-600 mb-2">6+</div>
                  <p className="text-gray-600 font-semibold">Years of Excellence</p>
                </div>
              </div>
            </div>

            {/* Content Side */}
            <div data-aos="fade-left">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-5 py-2.5 rounded-full text-sm font-bold mb-6">
                <SafeIcon icon={FiHeart} className="w-4 h-4" />
                <span>Welcome to Excellence</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
                Where Learning Meets
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600"> Innovation</span>
              </h2>
              
              <p className="text-lg text-gray-600 leading-relaxed mb-5">
                THE ASIAN INTERNATIONAL SCHOOL is dedicated to nurturing young learners through a unique blend 
                of <span className="font-bold text-purple-600">love, learning, and play</span>. We believe that the early years form the foundation for a child's lifelong growth.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed mb-5">
                Our approach focuses on <span className="font-bold text-purple-600">stimulating curiosity, encouraging creativity</span>, and building confidence 
                in every child. With a thoughtfully designed curriculum and a team of passionate educators, we provide a 
                joyful, safe, and inclusive environment.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Our goal is to help children <span className="font-bold text-purple-600">explore, imagine, and grow</span> — intellectually, emotionally, and socially — 
                while having fun every step of the way.
              </p>

              {/* Feature Pills */}
              <div className="flex flex-wrap gap-3 mb-8">
                <div className="flex items-center space-x-2 bg-purple-50 px-4 py-2 rounded-full">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-900">CBSE Curriculum</span>
                </div>
                <div className="flex items-center space-x-2 bg-pink-50 px-4 py-2 rounded-full">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-pink-600" />
                  <span className="text-sm font-semibold text-pink-900">STEM Focus</span>
                </div>
                <div className="flex items-center space-x-2 bg-cyan-50 px-4 py-2 rounded-full">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-cyan-600" />
                  <span className="text-sm font-semibold text-cyan-900">Safe Environment</span>
                </div>
              </div>
              
              <a 
                href="/contact" 
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-2xl font-bold hover:shadow-2xl transform hover:scale-105 transition-all duration-200"
              >
                <span>Discover Our Programs</span>
                <SafeIcon icon={FiCheckCircle} className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Founder - Modern Design */}
      <section className="py-24 bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section Header */}
          <div className="text-center mb-16" data-aos="fade-up">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-5 py-2.5 rounded-full text-sm font-bold mb-4">
              <SafeIcon icon={FiUsers} className="w-4 h-4" />
              <span>Leadership</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Meet Our Founder & Director</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Visionary leadership shaping the future of early childhood education
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Side with Modern Card */}
            <div className="order-2 lg:order-1" data-aos="fade-right">
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 rounded-3xl opacity-20 blur-3xl animate-pulse"></div>
                
                {/* Main Card */}
                <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white">
                  <img 
                    src="/img/about/ceo.png" 
                    alt="Sarika Reddy Kolan - Founder & Director" 
                    className="w-full h-[700px] object-cover"
                  />
                  
                  {/* Gradient Overlay at Bottom */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-8">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-3xl font-black text-white mb-2">Sarika Reddy Kolan</h3>
                        <p className="text-white/90 font-semibold text-lg mb-4">Founder & Director</p>
                        <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-lg px-4 py-2 rounded-full border border-white/20">
                          <SafeIcon icon={FiAward} className="w-4 h-4 text-yellow-300" />
                          <span className="text-white text-sm font-medium">MS in Telecommunications (Australia)</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Badge */}
                  <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-lg border border-white/20 text-white px-6 py-3 rounded-2xl shadow-2xl">
                    <p className="text-sm font-bold text-black/80">6+ Years Experience</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Side */}
            <div className="order-1 lg:order-2" data-aos="fade-left">
              <div className="space-y-6">
                {/* Qualifications */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiBook} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Academic Excellence</h4>
                      <p className="text-sm text-gray-600">World-Class Education</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Bachelor's in Electronics & Communication Engineering</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Master's in Telecommunications, RMIT University, Australia</span>
                    </li>
                  </ul>
                </div>

                {/* Vision & Passion */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiHeart} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Passionate Advocate</h4>
                      <p className="text-sm text-gray-600">STEM & STEAM Education</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Sarika Reddy Kolan is a visionary founder and passionate advocate for STEM and STEAM learning in early years. 
                    With over 8 years of experience in customer service leadership, she brings excellent communication and 
                    people-management skills into her educational approach.
                  </p>
                </div>

                {/* Mission */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiTarget} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Her Vision</h4>
                      <p className="text-sm text-gray-600">21st Century Skills</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Her vision for THE ASIAN INTERNATIONAL SCHOOL is to make STEM/STEAM accessible to all children by offering 
                    an integrated curriculum that builds a strong foundation in critical thinking, creativity, collaboration, 
                    and problem-solving—skills essential for the 21st century.
                  </p>
                </div>

                {/* Philosophy */}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border-2 border-orange-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiZap} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Educational Philosophy</h4>
                      <p className="text-sm text-gray-600">Learning Through Play</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    She believes in nurturing curiosity and instilling a lifelong love for learning through a balanced blend 
                    of play, experimentation, and exploration—shaping confident, capable, and innovative young minds ready to 
                    thrive in a technology-driven future.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Medical Director - Modern Design */}
      <section className="py-24 bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section Header */}
          <div className="text-center mb-16" data-aos="fade-up">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-5 py-2.5 rounded-full text-sm font-bold mb-4">
              <SafeIcon icon={FiUsers} className="w-4 h-4" />
              <span>Leadership</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Meet Our Director</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expert medical leadership ensuring holistic child development and wellness
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Side with Modern Card */}
            <div className="order-2 lg:order-1" data-aos="fade-right">
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 rounded-3xl opacity-20 blur-3xl animate-pulse"></div>
                
                {/* Main Card */}
                <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white">
                  <img 
                    src="/img/about/Mounika Redd.jpeg" 
                    alt="Dr. K. Mounika Reddy - Medical Director" 
                    className="w-full h-[700px] object-cover"
                  />
                  
                  {/* Gradient Overlay at Bottom */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-8">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-3xl font-black text-white mb-2">Dr. K. Mounika Reddy</h3>
                        <p className="text-white/90 font-semibold text-lg mb-4">Director – Asian International School</p>
                        <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-lg px-4 py-2 rounded-full border border-white/20">
                          <SafeIcon icon={FiAward} className="w-4 h-4 text-yellow-300" />
                          <span className="text-white text-sm font-medium">MBBS, MS (OBG), IMA Fellowship Infertility</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Badge */}
                  <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-lg border border-white/20 text-white px-6 py-3 rounded-2xl shadow-2xl">
                    <p className="text-sm font-bold text-black/80">Medical Expert</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Side */}
            <div className="order-1 lg:order-2" data-aos="fade-left">
              <div className="space-y-6">
                {/* Qualifications */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiBook} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Medical Excellence</h4>
                      <p className="text-sm text-gray-600">Consultant in Obstetrics & Gynaecology</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>MBBS from MNR Medical College, Telangana</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>MS in Obstetrics & Gynaecology from MNR Medical College</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>IMA Fellowship in Infertility</span>
                    </li>
                  </ul>
                </div>

                {/* Professional Experience */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiHeart} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Clinical Expertise</h4>
                      <p className="text-sm text-gray-600">Consultant at Fernandez Hospital</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Dr. K. Mounika Reddy is a distinguished Consultant in Obstetrics at Fernandez Hospital, 
                    bringing extensive medical expertise and compassionate care to her practice. Her commitment 
                    to women's health and family wellness extends beyond clinical practice into educational leadership.
                  </p>
                </div>

                {/* Educational Vision */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiTarget} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Her Educational Vision</h4>
                      <p className="text-sm text-gray-600">Holistic Child Development</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    As Director of Asian International School, Dr. Mounika brings her deep commitment to care, 
                    well-being, and holistic development into the educational sphere. She strongly believes that 
                    education should nurture not only academic excellence but also emotional strength, values, 
                    and overall wellness.
                  </p>
                </div>

                {/* Mission */}
                {/* <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border-2 border-orange-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiZap} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Creating Inspiring Environments</h4>
                      <p className="text-sm text-gray-600">Safe, Inclusive, and Nurturing</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Her vision focuses on creating a safe, inclusive, and inspiring environment where every child 
                    can learn, grow, and thrive with confidence. Through her medical expertise and educational 
                    leadership, she ensures that wellness and development go hand in hand.
                  </p>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission - Modern Cards */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-50 rounded-full filter blur-3xl opacity-50 -ml-48"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-50 rounded-full filter blur-3xl opacity-50 -mr-48"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16" data-aos="fade-up">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 px-5 py-2.5 rounded-full text-sm font-bold mb-4">
              <SafeIcon icon={FiTarget} className="w-4 h-4" />
              <span>Our Purpose</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Vision & Mission</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Driving educational excellence through innovation and compassion
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Vision Card */}
            <div className="group relative" data-aos="fade-right">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl opacity-75 group-hover:opacity-100 blur transition duration-500"></div>
              <div className="relative bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-10 h-full">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
                
                <div className="relative z-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-lg rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                    <SafeIcon icon={FiEye} className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-3xl font-black text-white mb-4">Our Vision</h3>
                  
                  <p className="text-xl font-bold text-blue-100 mb-5">
                    Empowering Young Minds Through STEM and Play-Based Learning
                  </p>
                  
                  <p className="text-blue-50 leading-relaxed mb-4">
                    At THE ASIAN INTERNATIONAL SCHOOL, our vision is to spark curiosity, creativity, and confidence in every child. 
                    We believe in building strong foundations through an integrated curriculum that blends STEM education with 
                    hands-on play and exploration.
                  </p>
                  
                  <p className="text-blue-50 leading-relaxed">
                    We envision a world where every child has the opportunity to discover their potential and grow into resilient, 
                    compassionate, and capable global citizens ready for a dynamic, technology-driven future.
                  </p>

                  {/* Feature Bullets */}
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white font-medium">Critical Thinking & Innovation</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white font-medium">Holistic Child Development</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white font-medium">Future-Ready Skills</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mission Card */}
            <div className="group relative" data-aos="fade-left">
              <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl opacity-75 group-hover:opacity-100 blur transition duration-500"></div>
              <div className="relative bg-gradient-to-br from-emerald-600 to-teal-600 rounded-3xl p-10 h-full">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
                
                <div className="relative z-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-lg rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                    <SafeIcon icon={FiTarget} className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-3xl font-black text-white mb-4">Our Mission</h3>
                  
                  <p className="text-xl font-bold text-emerald-100 mb-5">
                    Excellence in Early Childhood Education
                  </p>
                  
                  <p className="text-emerald-50 leading-relaxed mb-4">
                    Our mission is to deliver world-class education by combining modern teaching methods with traditional values. 
                    We aim to provide every child with the opportunity to excel academically, socially, and emotionally.
                  </p>
                  
                  <p className="text-emerald-50 leading-relaxed">
                    By focusing on holistic development, we ensure that each child's unique potential is nurtured and maximized. 
                    We are committed to creating an environment where children feel valued, inspired, and empowered to reach 
                    their full potential.
                  </p>

                  {/* Feature Bullets */}
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white font-medium">Academic Excellence</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white font-medium">Character Building</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white font-medium">Personalized Care</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Simplified */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <div className="inline-flex items-center space-x-2 bg-white px-5 py-2.5 rounded-full text-purple-700 font-bold mb-4 shadow-sm">
              <SafeIcon icon={FiStar} className="w-4 h-4" />
              <span>Why Choose Us</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Why We're the
              <span className="text-purple-600"> Best Choice</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover what makes THE ASIAN INTERNATIONAL SCHOOL the perfect choice for your child's educational journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-purple-200 transition-colors">
                  <SafeIcon icon={item.icon} className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6T Learning Approach - Redesigned */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-rose-50 rounded-full filter blur-3xl opacity-50 -ml-48 -mb-48"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content Side */}
            <div data-aos="fade-right">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-rose-100 to-pink-100 text-rose-700 px-5 py-2.5 rounded-full text-sm font-bold mb-6">
                <SafeIcon icon={FiBook} className="w-4 h-4" />
                <span>Innovative Methodology</span>
              </div>

              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
                The 6T Learning
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-pink-600"> Framework</span>
              </h2>

              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Our revolutionary 6T system uses play-based learning to teach big ideas, creating a comprehensive 
                educational experience that nurtures every aspect of your child's development.
              </p>

              {/* 6T Points Grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                {learningApproach.map((item, index) => (
                  <div 
                    key={index}
                    className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 border-2 border-${item.color}-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 group cursor-pointer`}
                    data-aos="fade-up" 
                    data-aos-delay={index * 100}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 bg-gradient-to-r from-${item.color}-500 to-${item.color}-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                        <SafeIcon icon={item.icon} className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-black text-${item.color}-900 text-lg mb-1`}>{item.title}</h4>
                        <p className={`text-${item.color}-700 text-sm leading-relaxed`}>{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-100">
                <p className="text-gray-700 leading-relaxed">
                  <span className="font-bold text-purple-600">💡 Our Approach:</span> We integrate these six essential elements to create 
                  a balanced, engaging, and effective learning environment that prepares children for academic success and lifelong learning.
                </p>
              </div>
            </div>

            {/* Image Side */}
            <div data-aos="fade-left" className="relative">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-rose-500 to-pink-500 rounded-3xl opacity-20 blur-2xl"></div>
                <div className="relative bg-white rounded-3xl shadow-2xl p-8 border-4 border-white">
                  <img
                    src="/img/about/6t.png"
                    alt="6T Learning Process Diagram"
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -top-6 -right-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-8 py-4 rounded-2xl shadow-2xl transform rotate-3">
                <p className="text-sm font-medium">Proven</p>
                <p className="text-2xl font-black">Methodology</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities - State-of-the-Art Learning Environments */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16" data-aos="fade-up">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <SafeIcon icon={FiMonitor} className="w-8 h-8 text-emerald-600" />
              <span className="text-emerald-600 font-semibold text-lg">World-Class Facilities</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              State-of-the-Art Learning{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                Environments
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our modern facilities are designed to provide the perfect learning environment,
              equipped with cutting-edge technology and resources that inspire curiosity and innovation.
            </p>
          </div>

          {/* Facilities Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {facilities.map((facility, index) => (
              <div
                key={facility.title}
                className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                data-aos="fade-up"
                data-aos-delay={index * 150}
              >
                <div className="lg:flex">
                  {/* Image */}
                  <div className="lg:w-1/2 h-64 lg:h-auto relative overflow-hidden">
                    <img
                      src={facility.image}
                      alt={`${facility.title} - Premium early childhood education program`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>

                    {/* Floating Icon */}
                    <div className={`absolute top-4 left-4 w-12 h-12 rounded-xl bg-gradient-to-r ${facility.color} flex items-center justify-center`}>
                      <SafeIcon icon={facility.icon} className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="lg:w-1/2 p-8 flex flex-col justify-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors duration-200">
                      {facility.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed mb-6">
                      {facility.description}
                    </p>

                    {/* Benefits List */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${facility.color}`}></div>
                        <span className="text-gray-700 text-sm">Expert guidance and supervision</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${facility.color}`}></div>
                        <span className="text-gray-700 text-sm">Age-appropriate activities and materials</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${facility.color}`}></div>
                        <span className="text-gray-700 text-sm">Safe and nurturing environment</span>
                      </div>
                    </div>

                    {/* Hover Effect */}
                    <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className={`w-full h-1 bg-gradient-to-r ${facility.color} rounded-full`}></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center bg-white rounded-3xl p-12 shadow-lg" data-aos="fade-up" data-aos-delay="600">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Give Your Child the Best Start?
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Join our community of caring families and watch your child flourish in a nurturing,
              stimulating environment designed for their unique growth and development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+917416706111"
                className="inline-flex items-center justify-center space-x-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <span>Schedule a Visit</span>
              </a>
              <a
                href="https://wa.me/917416706111"
                className="inline-flex items-center justify-center space-x-3 bg-green-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-green-600 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <span>Chat with Us</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation - Modern Highlight */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-50 rounded-full filter blur-3xl opacity-50 -mr-48"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl overflow-hidden shadow-2xl" data-aos="fade-up">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left Content */}
              <div className="p-12 lg:p-16 flex flex-col justify-center">
                <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-lg px-5 py-2.5 rounded-full text-sm font-bold text-white mb-6 w-fit">
                  <SafeIcon icon={FiAward} className="w-4 h-4" />
                  <span>Certified & Recognised</span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
                  Recognised & Trusted by Authorities
                </h2>
                
                <p className="text-xl text-emerald-50 mb-8 leading-relaxed">
                  We follow the <span className="font-bold bg-white/20 px-2 py-1 rounded">CBSE Curriculum</span> and are 
                  proud to be <span className="font-bold bg-white/20 px-2 py-1 rounded">Recognised by the Government of Telangana</span>
                </p>

                {/* Certification Badges */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
                    <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiAward} className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Education Board</p>
                      <p className="text-2xl font-black text-white">CBSE Affiliated</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
                    <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiShield} className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Government Recognition</p>
                      <p className="text-2xl font-black text-white">Telangana Approved</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
                    <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiBook} className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Academic Programs</p>
                      <p className="text-2xl font-black text-white">Nursery to Grade 7</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Stats */}
              <div className="bg-white/10 backdrop-blur-lg p-12 lg:p-16 flex flex-col justify-center border-l border-white/20">
                <h3 className="text-3xl font-black text-white mb-8">Quick Facts</h3>
                <div className="space-y-6">
                  {stats.map((stat, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className={`w-14 h-14 bg-gradient-to-r ${stat.gradient} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                        <SafeIcon icon={stat.icon} className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <p className="text-4xl font-black text-white mb-1">{stat.number}</p>
                        <p className="text-emerald-100 font-semibold">{stat.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Awards & Recognition - Modern Gallery */}
      <section className="py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle, #fbbf24 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16" data-aos="fade-up">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 px-5 py-2.5 rounded-full text-sm font-bold mb-4">
              <SafeIcon icon={FiAward} className="w-4 h-4" />
              <span>Recognition & Achievements</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Awards &
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600"> Recognition</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Celebrating our achievements and recognition for excellence in early childhood education
            </p>
          </div>

          {/* Awards Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {awards.map((award, index) => (
              <div
                key={index}
                className="group relative"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 h-full">
                  {/* Image */}
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={award.image}
                      alt={award.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    
                    {/* Icon Badge */}
                    <div className="absolute top-4 right-4 w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center shadow-lg">
                      <SafeIcon icon={award.icon} className="w-6 h-6 text-white" />
                    </div>

                    {/* Title Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-2xl font-black text-white mb-2">{award.title}</h3>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="p-6">
                    <p className="text-gray-600 leading-relaxed">{award.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Simple & Full Width */}
      <section className="py-10 bg-gradient-to-br from-emerald-500 to-teal-600 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
          <div className="absolute top-40 right-20 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-32 left-1/2 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center" data-aos="fade-up">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-lg border border-white/30 px-6 py-3 rounded-full mb-8">
              <SafeIcon icon={FiHeart} className="w-5 h-5 text-white" />
              <span className="text-white font-bold">Join Our Learning Community</span>
            </div>

            {/* Heading */}
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              Ready to Begin Your Child's Amazing Learning Journey?
            </h2>

            {/* Subheading */}
            <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
              Join us for Academic Year 2026 and give your child the gift of world-class education
              in a nurturing, innovative environment
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <a
                href="/contact"
                className="inline-flex items-center justify-center space-x-3 bg-white text-teal-600 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-teal-50 transform hover:scale-105 transition-all duration-200 shadow-2xl"
              >
                <span>Schedule Campus Tour</span>
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6" />
              </a>

              <a
                href="tel:+918106921133"
                className="inline-flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-lg border-2 border-white text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white/20 transform hover:scale-105 transition-all duration-200"
              >
                <SafeIcon icon={FiPhone} className="w-6 h-6" />
                <span>Call: 81069 21133</span>
              </a>
            </div>

            {/* Trust Indicators */}
            {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="flex flex-col items-center space-y-3 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-300" />
                <span className="text-white font-bold text-center">CBSE Curriculum</span>
              </div>
              <div className="flex flex-col items-center space-y-3 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-300" />
                <span className="text-white font-bold text-center">Govt. Recognised</span>
              </div>
              <div className="flex flex-col items-center space-y-3 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-300" />
                <span className="text-white font-bold text-center">ISO Certified</span>
              </div>
              <div className="flex flex-col items-center space-y-3 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-300" />
                <span className="text-white font-bold text-center">500+ Happy Students</span>
              </div>
            </div> */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;